export type UsageMonthRow = {
  key: string;
  label: string;
  used: number;
  limit: number;
  unlimited: boolean;
  // limit: used / limit · count: connected count, no cap · access: on/off
  // feature with nothing metered ("Included") · group: header row
  kind: 'limit' | 'count' | 'access' | 'group';
  enabled: boolean;
  subRows?: UsageMonthRow[];
  overridden?: { planValue: number; overriddenValue: number } | null;
  /** `used` is a floor (a capped count), shown as "N+". */
  approximate?: boolean;
  /** For `count` rows: "connected" for integrations, otherwise a bare number. */
  countUnit?: 'connected';
  /** Usage fetched separately and not in yet, or failed. */
  status?: 'loading' | 'error';
};

const LABELS: Record<string, string> = {
  inbox: 'Inbox',
  contacts: 'Contacts',
  contact_tags: 'Contact Tags',
  templates: 'Templates',
  campaigns: 'Campaigns',
  whatsapp_flows: 'WhatsApp Flows',
  automations: 'Automations',
  automation_flows: 'Automations',
  business_accounts: 'Business Accounts',
  analytics: 'Analytics',
  commerce: 'Commerce',
  integrations: 'Integrations',
  messages: 'Messages (this month)',
  messages_per_month: 'Messages (this month)',
  team_management: 'Team Management',
  max_admins: 'Max Admins',
  max_users: 'Max Users',
  max_agents: 'Max Agents',
  team_members: 'Team Members',
  ai_generation: 'AI Credits',
  data_store: 'Data Store Tables',
  ads: 'Meta Ad Accounts',
  moengage: 'MoEngage',
};

// Sidebar order (AppSidebar primary menu), then the sections that live under
// Settings. Keys not listed here follow, in plan order.
const ORDER = [
  // Inbox
  'inbox',
  'messages',
  'messages_per_month',
  // Campaigns
  'campaigns',
  'templates',
  // CRM
  'contacts',
  'contact_tags',
  'leads',
  'notes_and_tags',
  'contact_export',
  // Automations (Automations, Forms)
  'automations',
  'automation_flows',
  'whatsapp_flows',
  // Appointments
  'appointments',
  // Commerce
  'commerce',
  // Ads
  'ads',
  // AI Agents
  'ai_generation',
  'whatsapp_ai_agent',
  'ai_calling',
  // POS
  'petpooja',
  // Data Store, Reports
  'data_store',
  'analytics',
  // Settings
  'team_management',
  'integrations',
  'business_accounts',
] as const;

const GROUP_KEYS = new Set(['integrations', 'team_management']);

// Integrations the plan and override forms only switch on or off — their
// stored "1" means enabled, not a cap — so the row shows a connected count.
// Instagram / Facebook / LinkedIn / YouTube carry real connect caps.
const ENABLE_ONLY_KEYS = new Set([
  'shopify',
  'woocommerce',
  'razorpay',
  'cashfree',
  'payu',
  'whatsapp_payments',
  'customapi',
  'zapier',
  'moengage',
  'stripe',
  'tiktok_ads',
  'tiktok_content',
  'petpooja',
]);

// Resolved limit keys that are derived from, or shown inside, another row.
const DERIVED_LIMIT_KEYS = new Set([
  'users',
  'automation_flows',
  'analytics_level',
  'meta_ad_accounts',
  'messages_per_month',
]);

const TEAM_USAGE_KEY: Record<string, string> = {
  max_admins: 'admins',
  max_users: 'users',
  max_agents: 'agents',
};

export function humaniseFeatureKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function labelFor(key: string): string {
  return LABELS[key] || humaniseFeatureKey(key);
}

function orderIndex(key: string): number {
  const i = (ORDER as readonly string[]).indexOf(key);
  return i === -1 ? ORDER.length : i;
}

// Plan feature keys whose resolved limit is stored under a different name
// (backend catalog.js STORAGE_LIMIT_KEY).
const RESOLVED_LIMIT_KEY: Record<string, string> = {
  automations: 'automation_flows',
  analytics: 'analytics_level',
};

export function resolvedLimitKey(planKey: string): string {
  return RESOLVED_LIMIT_KEY[planKey] || planKey;
}

// Resolved entitlements (plan + add-ons + overrides) win; the raw plan is only
// a fallback for keys the resolver did not emit.
function planFeatureEnabled(
  key: string,
  parsedPlanFeatures: Record<string, unknown> | null | undefined,
  entitlementsFeatures: Record<string, unknown> | null | undefined,
): boolean {
  const resolved = entitlementsFeatures?.[key];
  if (typeof resolved === 'boolean') return resolved;
  const raw = parsedPlanFeatures?.[key];
  if (raw && typeof raw === 'object' && 'enabled' in (raw as object)) {
    return (raw as { enabled?: boolean }).enabled === true;
  }
  return !!entitlementsFeatures?.[key];
}

function usageKeyForPlanKey(planKey: string): string {
  if (planKey === 'messages_per_month') return 'messages';
  if (planKey === 'automation_flows') return 'automations';
  return planKey;
}

// `metered: false` marks an entry whose limit is known but whose usage the
// API does not measure (on/off features).
type UsageMap = Record<string, { used?: number; limit?: number | null; metered?: boolean; approximate?: boolean; status?: 'loading' | 'error'; overridden?: { planValue: number; overriddenValue: number } | null } | undefined>;

function coerceLimit(raw: unknown): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const n = Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function limitOf(usage: { limit?: number | null } | undefined): number {
  return coerceLimit(usage?.limit);
}

function planDeclaredLimit(rawValue: unknown): number | null {
  if (!rawValue || typeof rawValue !== 'object') return null;
  if (!('limit' in (rawValue as object))) return null;
  const n = coerceLimit((rawValue as { limit?: unknown }).limit);
  return n === -1 || n > 0 ? n : null;
}

function buildLeafRow(args: {
  planKey: string;
  usageKey: string;
  label: string;
  currentUsage: UsageMap;
  enabled: boolean;
  planValue?: unknown;
  resolvedLimit?: number;
  featureOverride?: { planValue: unknown; overriddenValue: unknown } | null;
}): UsageMonthRow {
  const { planKey, usageKey, label, currentUsage, enabled, planValue, resolvedLimit, featureOverride } = args;
  const usage = currentUsage[usageKey];
  const meteredLimit = limitOf(usage);
  const limit =
    meteredLimit !== 0
      ? meteredLimit
      : resolvedLimit !== undefined
        ? resolvedLimit
        : (planDeclaredLimit(planValue) ?? 0);
  const unlimited = limit === -1;
  const hasLimitRow = unlimited || limit > 0;
  // Nothing measures this row's usage — never show an invented 0.
  const metered = usage !== undefined && usage.metered !== false;
  const enableOnly = ENABLE_ONLY_KEYS.has(planKey);

  // An on/off override (feature switched on or off) with no limit change.
  const toggleOverride = featureOverride
    ? { planValue: featureOverride.planValue ? 1 : 0, overriddenValue: featureOverride.overriddenValue ? 1 : 0 }
    : null;

  return {
    key: usageKey || planKey,
    label,
    used: usage?.used ?? 0,
    limit,
    unlimited: unlimited || (!hasLimitRow && enabled),
    // Metered with no limit (e.g. enabled by an override that set none):
    // show the count itself rather than hiding it behind "Included".
    kind: !metered ? 'access' : enableOnly || !hasLimitRow ? 'count' : 'limit',
    countUnit: enableOnly ? 'connected' : undefined,
    enabled: enabled || hasLimitRow,
    overridden: usage?.overridden ?? toggleOverride,
    approximate: usage?.approximate === true,
    status: usage?.status,
  };
}

function subFeatureKeys(rawValue: unknown): string[] {
  const sub = (rawValue as { sub_features?: unknown } | null | undefined)?.sub_features;
  return sub && typeof sub === 'object' ? Object.keys(sub as object) : [];
}

// A resolved sub-feature is a bare number (-1 unlimited, 0 off, n cap) or,
// on the expired-plan path, the plan's own {enabled, limit} object.
function resolvedSubFeature(
  entitlementsSubFeatures: Record<string, unknown> | null | undefined,
  parentKey: string,
  subKey: string,
): { enabled: boolean; planValue: unknown } | null {
  const parent = entitlementsSubFeatures?.[parentKey];
  if (!parent || typeof parent !== 'object') return null;
  const value = (parent as Record<string, unknown>)[subKey];
  if (value === undefined || value === null) return null;
  if (typeof value === 'object') {
    return { enabled: (value as { enabled?: boolean }).enabled === true, planValue: value };
  }
  const n = coerceLimit(value);
  return { enabled: n !== 0, planValue: { enabled: n !== 0, limit: n } };
}

function subFeatureEnabled(rawValue: unknown, subKey: string): boolean {
  const sub = (rawValue as { sub_features?: Record<string, unknown> } | null | undefined)
    ?.sub_features?.[subKey];
  if (sub && typeof sub === 'object' && 'enabled' in (sub as object)) {
    return (sub as { enabled?: boolean }).enabled === true;
  }
  return !!sub;
}

export function buildUsageMonthRows(args: {
  currentUsage: UsageMap;
  parsedPlanFeatures?: Record<string, unknown> | null;
  entitlementsFeatures?: Record<string, unknown> | null;
  entitlementsLimits?: Record<string, unknown> | null;
  entitlementsSubFeatures?: Record<string, unknown> | null;
  /** overrideEffects.features — on/off changes an override made. */
  featureOverrides?: Record<string, { planValue: unknown; overriddenValue: unknown }> | null;
}): UsageMonthRow[] {
  const {
    currentUsage,
    parsedPlanFeatures,
    entitlementsFeatures,
    entitlementsLimits,
    entitlementsSubFeatures,
    featureOverrides,
  } = args;

  const resolvedLimitFor = (planKey: string): number | undefined => {
    // AI Generation's allowance is its ai_credits sub-feature; the top-level
    // ai_generation limit is not read by anything.
    if (planKey === 'ai_generation') {
      const resolved = resolvedSubFeature(entitlementsSubFeatures, 'ai_generation', 'ai_credits');
      return resolved ? limitOf(resolved.planValue as { limit?: number | null }) : undefined;
    }
    // Data Store's allowance is Max tables; its top-level limit is not read.
    if (planKey === 'data_store') {
      const resolved = resolvedSubFeature(entitlementsSubFeatures, 'data_store', 'max_tables');
      return resolved ? limitOf(resolved.planValue as { limit?: number | null }) : undefined;
    }
    const raw = entitlementsLimits?.[resolvedLimitKey(planKey)];
    return raw === undefined || raw === null ? undefined : coerceLimit(raw);
  };

  const planKeys = parsedPlanFeatures
    ? Object.keys(parsedPlanFeatures).filter((k) => !['features', 'limits'].includes(k))
    : [];

  // Features granted by an add-on or override without the plan listing them
  // (e.g. contact_export) still need a row.
  const grantedKeys = Object.keys(entitlementsLimits || {}).filter(
    (k) => !DERIVED_LIMIT_KEYS.has(k) && entitlementsFeatures?.[k] === true,
  );

  const candidates = Array.from(new Set<string>([...planKeys, ...ORDER, ...grantedKeys]));

  const preferDeclared = (alias: string, canonical: string): string[] =>
    planKeys.includes(alias) ? [canonical] : [];
  const skip = new Set<string>([
    ...preferDeclared('messages_per_month', 'messages'),
    ...preferDeclared('automation_flows', 'automations'),
  ]);

  const emitted = new Set<string>();

  const rows: UsageMonthRow[] = [];

  for (const planKey of candidates.sort((a, b) => orderIndex(a) - orderIndex(b))) {
    if (skip.has(planKey)) continue;

    const rawValue = parsedPlanFeatures?.[planKey];
    const enabled = planFeatureEnabled(planKey, parsedPlanFeatures, entitlementsFeatures);

    // Switched off for this org (plan or override) — no quota to show.
    if (entitlementsFeatures?.[planKey] === false) continue;

    if (GROUP_KEYS.has(planKey)) {
      // Plan-declared sub-features plus any an add-on or override introduced;
      // Team shows only the per-role seats that are enforced.
      const resolvedParent = entitlementsSubFeatures?.[planKey];
      const allKeys = Array.from(
        new Set([
          ...subFeatureKeys(rawValue),
          ...(resolvedParent && typeof resolvedParent === 'object' ? Object.keys(resolvedParent) : []),
        ]),
      );
      const keys = planKey === 'team_management' ? allKeys.filter((k) => k in TEAM_USAGE_KEY) : allKeys;
      const subRows = keys
        .map((subKey) => {
          const resolved = resolvedSubFeature(entitlementsSubFeatures, planKey, subKey);
          return buildLeafRow({
            planKey: subKey,
            usageKey: TEAM_USAGE_KEY[subKey] || subKey,
            label: labelFor(subKey),
            currentUsage,
            enabled: resolved ? resolved.enabled : subFeatureEnabled(rawValue, subKey),
            planValue: resolved
              ? resolved.planValue
              : (rawValue as { sub_features?: Record<string, unknown> } | null)
                  ?.sub_features?.[subKey],
          });
        })
        .filter((row) => row.enabled || row.limit !== 0 || row.unlimited);

      // Legacy plan: a bare team_management limit, no per-role seats — show
      // the pooled seat count instead of dropping the section.
      if (subRows.length === 0 && planKey === 'team_management' && currentUsage.team_members) {
        if (emitted.has('team_members')) continue;
        emitted.add('team_members');
        rows.push(
          buildLeafRow({
            planKey: 'team_members',
            usageKey: 'team_members',
            label: labelFor('team_members'),
            currentUsage,
            enabled,
          }),
        );
        continue;
      }

      if (subRows.length === 0) continue;
      if (emitted.has(planKey)) continue;
      emitted.add(planKey);

      rows.push({
        key: planKey,
        label: labelFor(planKey),
        used: 0,
        limit: 0,
        unlimited: false,
        kind: 'group',
        enabled: true,
        subRows,
      });
      continue;
    }

    const usageKey = usageKeyForPlanKey(planKey);
    const usage = currentUsage[usageKey];
    const limit = limitOf(usage);
    const unlimited = limit === -1;

    if (!enabled && !usage) continue;
    if (!enabled && limit === 0 && !unlimited) continue;

    if (emitted.has(usageKey)) continue;
    emitted.add(usageKey);

    rows.push(
      buildLeafRow({
        planKey,
        usageKey,
        label: labelFor(planKey),
        currentUsage,
        enabled,
        planValue: rawValue,
        resolvedLimit: resolvedLimitFor(planKey),
        featureOverride: featureOverrides?.[planKey] ?? null,
      }),
    );
  }

  return rows;
}

export function usagePercent(used: number, limit: number): number {
  if (limit === -1 || limit <= 0) return 0;
  const pct = Math.round((used / limit) * 100);
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, pct));
}

export function formatUsageLimit(limit: number | null | undefined): string {
  if (limit === null || limit === undefined) return 'N/A';
  if (limit === -1) return '∞';
  if (limit >= 1000) return `${limit / 1000}K`;
  return String(limit);
}
